import {Component, computed, inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormMode} from '../../../shared/types/common';
import {DrawerFormBase} from '../drawer-form-base';
import {ManagementStore} from '../management.store';
import {ManagementService} from '../management.service';
import {AuthorizationService} from '../../../services/authorization.service';
import {Permissions} from '../../../shared/permissions';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions} from '../../../utils/mappings';
import {toSnakeCase} from '../../../utils/strings';
import {Account, AccountPayload, AccountUsersResponse, BrandSummary} from '../types';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';

@Component({
  selector: 'app-account-drawer',
  imports: [ReactiveFormsModule, CommonModule, TitledInputComponent, ButtonComponent, SelectMenuComponent, LoaderComponent],
  templateUrl: './account-drawer.component.html',
  styleUrl: './account-drawer.component.scss',
})
export class AccountDrawerComponent extends DrawerFormBase<Account> {
  public form: FormGroup<{
    name: FormControl<string | null>;
    key: FormControl<string | null>;
    brands: FormControl<string[] | null>;
  }>;
  public readonly configs = {
    [FormMode.ADD]: {title: 'Add account', submitText: 'Add', submit: () => this.addAccount()},
    [FormMode.EDIT]: {title: 'Edit account', submitText: 'Update', submit: () => this.editAccount()},
  };

  public accountUsers?: string[];
  public canDelete = false;
  protected readonly SelectMode = SelectMode;

  // Options derived from the store's cached brand summaries — reactive, so they fill in as soon as
  // the (lazily fetched, then cached) list resolves.
  protected readonly brandOptions = computed(() => objectArrayToSelectOptions(this.store.brandSummaries(), 'brandKey', 'name'));

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(ManagementStore);
  private readonly api = inject(ManagementService);
  private readonly authorizationService = inject(AuthorizationService);

  constructor() {
    super();
    this.canDelete = this.authorizationService.hasPermissions([Permissions.DELETE_ACCOUNT]);
    this.store.ensureBrandSummaries();

    this.form = this.fb.group({
      key: new FormControl({value: '', disabled: !!this.entity}, [CustomValidators.required('Account key'), CustomValidators.brandKey('Account key')]),
      name: new FormControl('', {validators: [CustomValidators.required('Account name')], updateOn: 'blur'}),
      brands: new FormControl<string[]>([]),
    });

    if (this.entity) {
      this.currentMode = FormMode.EDIT;
      this.api.getAccountUsers(this.entity.accountKey)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: users => (this.accountUsers = this.userResponseToStringArray(users)),
          error: () => (this.accountUsers = []),
        });
      this.form.patchValue({
        ...this.entity,
        key: this.entity.accountKey,
        brands: ((this.entity.brands ?? []) as unknown as BrandSummary[]).map(brand => brand.brandKey),
      });
    }

    // In add mode, mirror the typed name into the key as a snake_case suggestion.
    this.form.get('name')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!this.entity && value) {
          this.form.get('key')?.setValue(toSnakeCase(value), {emitEvent: false});
        }
      });
  }

  get key(): FormControl {
    return this.form.get('key') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get brands(): FormControl<string[]> {
    return this.form.get('brands') as FormControl<string[]>;
  }

  submit(): void {
    if (this.saving() || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.configs[this.currentMode].submit();
  }

  private addAccount(): void {
    this.store.addAccount(this.getAccountPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.onSuccess(`Account "${result.name}" added successfully`),
        error: error => this.onError(error),
      });
  }

  private editAccount(): void {
    this.store.editAccount(this.getAccountPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.onSuccess(`Account "${result.name}" updated successfully`),
        error: error => this.onError(error),
      });
  }

  deleteAccount(): void {
    if (!this.entity) {
      return;
    }
    // Only empty accounts can be deleted. The row already carries its users (loaded with elaborate),
    // so gate synchronously; fall back to the fetched users count. Surface the reason only on an
    // actual delete attempt, not as a standing warning while editing.
    if ((this.entity.userIds?.length ?? this.accountUsers?.length ?? 0) > 0) {
      this.utilService.openSnackBar('Remove all users before this account can be deleted.', {variant: 'error', position: 'center'});
      return;
    }
    this.confirmAndDelete(
      {
        header: 'Delete account',
        description: `This permanently deletes the account ${this.entity.name} (${this.entity.accountKey}) and its data. This action is irreversible.`,
        confirmLabel: 'Delete account',
        expectedValue: this.entity.name,
      },
      () => this.store.deleteAccount(this.entity!.accountKey),
      `Account "${this.entity.name}" deleted successfully`,
    );
  }

  private userResponseToStringArray(users: AccountUsersResponse): string[] {
    return users.accountUsers.map(user => `${user.firstName} ${user.lastName} (${user.email})`);
  }

  private getAccountPayload(): AccountPayload {
    const form = this.form.getRawValue();
    return {accountKey: form.key, name: form.name, brandKeys: form.brands} as AccountPayload;
  }
}
